// The supply projections in the docs: what losses mint, what the burn auction takes out, year after year.
// A model, not a forecast: it takes a steady flow of net losses, the deployed shares (stakers' share of a
// loss, the auction's share of every HYPE lot) and one assumption on the price PAPURR clears at, see `project`.

export type Regime = 'fixed' | 'curve';

export type ProjectionInput = {
  /** Net losses per month, USD (what the house keeps, the base of the mint and of the stakers' share) */
  lossesPerMonth: number;
  /** The yield holders demand on the value of their stake, percent per year: what makes them sell to the auction */
  yieldPct: number;
  /** Share of the supply that is staked, percent */
  stakedPct: number;
  /** 'fixed': the soft launch, `fixedRate` PAPURR per $1; 'curve': the tail of the mint curve from zero */
  regime: Regime;
  fixedRate: number;
  /** PAPURR per $1 at the start of the curve (100) and its decay constant K, USD */
  flatRate: number;
  tailKUsd: number;
  /** The auction's share of every HYPE lot (0.3) and the stakers' share of a loss (0.04) */
  auctionShare: number;
  stakerLossShare: number;
  years: number;
};

export type ProjectionRow = {
  year: number;
  /** PAPURR minted so far */
  minted: number;
  /** PAPURR burned so far */
  burned: number;
  supply: number;
  /** The clearing price the assumption implies at the end of the year, USD per PAPURR (NaN with no supply) */
  price: number;
};

/** The auction's HYPE budget per $1 of net loss, USD: its share of the stakers' share */
export function budgetPerDollar(auctionShare: number, stakerLossShare: number): number {
  return auctionShare * stakerLossShare;
}

/** The clearing price under which the auction burns more than a $1 loss mints at `rate` PAPURR per $1 */
export function breakEvenPrice(rate: number, auctionShare: number, stakerLossShare: number): number {
  return budgetPerDollar(auctionShare, stakerLossShare) / rate;
}

/**
 * The share of the staked supply the auction burns per year when holders sell at the value of the yield they
 * give up: the price is then (1 - a) R / (y x staked) and the pot a R buys a / (1 - a) x y x staked PAPURR,
 * whatever the revenue R.
 */
export function yearlyBurnShare(yieldPct: number, stakedPct: number, auctionShare: number): number {
  return (auctionShare / (1 - auctionShare)) * (yieldPct / 100) * (stakedPct / 100);
}

/** PAPURR per $1 on the tail of the curve after `tailUsd` of tail losses */
export function tailRate(tailUsd: number, flatRate: number, tailKUsd: number): number {
  const f = tailKUsd / (tailKUsd + tailUsd);
  return flatRate * f * f;
}

/** PAPURR the tail mints for `lossUsd` starting at `tailUsd`: the rate integrated, as MintMath does */
export function tailMint(tailUsd: number, lossUsd: number, flatRate: number, tailKUsd: number): number {
  return (flatRate * lossUsd * tailKUsd * tailKUsd) / ((tailKUsd + tailUsd) * (tailKUsd + tailUsd + lossUsd));
}

/** Tail losses after which the curve's rate is down to `rate` PAPURR per $1 */
export function tailUsdAtRate(rate: number, flatRate: number, tailKUsd: number): number {
  return tailKUsd * (Math.sqrt(flatRate / rate) - 1);
}

/**
 * Month by month: losses mint (at the fixed rate, or along the tail of the curve, the house assumed above the
 * flat threshold), and the auction burns the yearly share over twelve months, on the supply the month starts
 * with. Row 0 is the start; one row per year end.
 */
export function project(input: ProjectionInput): ProjectionRow[] {
  const { lossesPerMonth: loss, regime, fixedRate, flatRate, tailKUsd, auctionShare, stakerLossShare } = input;
  const burnPerMonth = yearlyBurnShare(input.yieldPct, input.stakedPct, auctionShare) / 12;
  const revenuePerYear = 12 * stakerLossShare * loss;
  const staked = input.stakedPct / 100;
  const y = input.yieldPct / 100;
  const priceOf = (supply: number) => (supply > 0 && y > 0 && staked > 0 ? ((1 - auctionShare) * revenuePerYear) / (y * staked * supply) : NaN);
  const rows: ProjectionRow[] = [{ year: 0, minted: 0, burned: 0, supply: 0, price: NaN }];
  let minted = 0;
  let burned = 0;
  let supply = 0;
  let tail = 0;
  for (let month = 1; month <= input.years * 12; month++) {
    const burn = supply * burnPerMonth;
    const mint = regime === 'fixed' ? loss * fixedRate : tailMint(tail, loss, flatRate, tailKUsd);
    if (regime === 'curve') tail += loss;
    minted += mint;
    burned += burn;
    supply += mint - burn;
    if (month % 12 === 0) rows.push({ year: month / 12, minted, burned, supply, price: priceOf(supply) });
  }
  return rows;
}

/** 1,234,567 -> 1.23M; 950,000 -> 950k; 2.5e9 -> 2.5B */
export function fmtCount(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  // two significant decimals under 10, one under 100, none above, trailing zeros dropped: 12M, 3.56M, 7.7M
  const f = (v: number, unit: string) => `${(v < 10 ? v.toFixed(2) : v < 100 ? v.toFixed(1) : v.toFixed(0)).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')}${unit}`;
  if (abs >= 1e9) return f(n / 1e9, 'B');
  if (abs >= 1e6) return f(n / 1e6, 'M');
  if (abs >= 1e3) return f(n / 1e3, 'k');
  return Math.round(n).toString();
}

/** A price in USD, kept to two significant digits however small */
export function fmtPrice(p: number): string {
  if (!Number.isFinite(p) || p <= 0) return '—';
  if (p >= 1) return `$${p.toFixed(2)}`;
  return `$${Number(p.toPrecision(2)).toString()}`;
}
